/**
 * `/search?q=…` — finding a file by name.
 *
 * Two things this screen has to be honest about, because both surprise people:
 *
 * - **Names only.** Nothing here searches inside a document. Somebody hunting
 *   for a phrase they remember will otherwise conclude the index is broken.
 * - **`hasMore` is not a next page.** Results are filtered by what the caller
 *   may read *after* the database match, so an offset would not mean the same
 *   thing twice and there is no cursor. `hasMore` means the query was too broad
 *   to answer completely — the fix is a narrower query, not another request.
 *
 * The query lives in the URL, so a search is a link somebody can send.
 */
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ResourceKind } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useDebounced';
import { MIN_SEARCH_LENGTH, contentUrl, listLibraries, searchResources } from '../../api/resources';

export default function SearchPage() {
  const { t } = useTranslation('files');
  const [params, setParams] = useSearchParams();

  const urlQuery = params.get('q') ?? '';
  const library = params.get('library') ?? '';
  const kind = (params.get('kind') ?? '') as ResourceKind | '';

  // Typed separately from the URL so each keystroke does not push a history
  // entry — the URL catches up once typing settles.
  const [term, setTerm] = useState(urlQuery);
  const debounced = useDebounced(term, 350);

  // Captured at mount, because `autoFocus` is only honoured when the input is
  // first created — a later value would silently do nothing.
  const arrivedEmpty = useRef(urlQuery === '');

  useEffect(() => {
    setTerm(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    if (debounced === urlQuery) return;
    const next = new URLSearchParams(params);
    if (debounced) next.set('q', debounced);
    else next.delete('q');
    setParams(next, { replace: true });
    // `params` is a fresh object each render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const libraries = useAsync(() => listLibraries(), []);

  // Driven by the URL, not by the local field. The header box also navigates
  // here as you type, and debouncing again on this side would stack its delay on
  // top of that one — the same search taking twice as long to appear when it
  // came from the header. `debounced` writes to the URL; the URL writes here.
  const results = useAsync(
    () =>
      searchResources({
        q: urlQuery,
        limit: 100,
        ...(library ? { library } : {}),
        ...(kind ? { kind } : {}),
      }),
    [urlQuery, library, kind],
    { enabled: urlQuery.trim().length >= MIN_SEARCH_LENGTH },
  );

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        title={t('search.title')}
        description={t('search.description')}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {/* `autoFocus` only when you arrive with nothing typed: the header box
            navigates here as you type, and focusing unconditionally would rip
            the caret out of it after the second character, sending the rest of
            the word to a different input. */}
        <SearchField
          value={term}
          onChange={setTerm}
          placeholder={t('search.placeholder')}
          autoFocus={arrivedEmpty.current}
          size="medium"
          fullWidth
        />
        <TextField
          select
          label={t('search.library')}
          value={library}
          onChange={(event) => setParam('library', event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('search.anyLibrary')}</MenuItem>
          {(libraries.data?.libraries ?? []).map((entry) => (
            <MenuItem key={entry.key} value={entry.key}>
              {entry.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t('search.kind')}
          value={kind}
          onChange={(event) => setParam('kind', event.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t('search.anyKind')}</MenuItem>
          <MenuItem value="file">{t('search.kindFiles')}</MenuItem>
          <MenuItem value="folder">{t('search.kindFolders')}</MenuItem>
        </TextField>
      </Stack>

      {urlQuery.trim().length < MIN_SEARCH_LENGTH ? (
        <EmptyState
          icon={<SearchIcon fontSize="inherit" />}
          title={t('search.promptTitle')}
          description={t('search.promptDescription')}
        />
      ) : (
        <AsyncContent state={results}>
          {({ results: rows, hasMore, libraries: searched }) => (
            <>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {/* Through i18next's plural machinery, not an inline ternary:
                      Czech needs three forms where English needs two. */}
                  {t('search.resultCount', { count: rows.length })}
                </Typography>
                {searched.map((key) => (
                  <Chip key={key} size="small" variant="outlined" label={key} />
                ))}
              </Stack>

              {hasMore && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('search.truncated')}
                </Alert>
              )}

              {rows.length === 0 ? (
                <EmptyState
                  icon={<SearchIcon fontSize="inherit" />}
                  title={t('search.noMatchTitle')}
                  description={t('search.noMatchDescription')}
                />
              ) : (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('search.column.name')}</TableCell>
                        <TableCell sx={{ width: 140 }}>{t('search.column.library')}</TableCell>
                        <TableCell sx={{ width: 160 }}>{t('search.column.owner')}</TableCell>
                        <TableCell sx={{ width: 140 }}>{t('search.column.changed')}</TableCell>
                        <TableCell sx={{ width: 64 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((resource) => (
                        <TableRow key={resource.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                              {resource.kind === 'folder' ? (
                                <FolderIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              ) : (
                                <InsertDriveFileOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: 'text.secondary' }}
                                />
                              )}
                              {resource.kind === 'folder' ? (
                                <Link
                                  component={RouterLink}
                                  to={`/files/${resource.library}/${resource.id}`}
                                  underline="hover"
                                  color="inherit"
                                >
                                  {resource.name}
                                </Link>
                              ) : (
                                <Link
                                  component={RouterLink}
                                  to={
                                    resource.parentId
                                      ? `/files/${resource.library}/${resource.parentId}`
                                      : `/files/${resource.library}`
                                  }
                                  underline="hover"
                                  color="inherit"
                                >
                                  {resource.name}
                                </Link>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {resource.library}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {resource.ownerName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              <Timestamp value={resource.updatedAt} />
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {resource.kind === 'file' && (
                              <Tooltip title={t('search.download')}>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={contentUrl(resource.id)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
              <Box sx={{ height: 24 }} />
            </>
          )}
        </AsyncContent>
      )}
    </>
  );
}
